import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { LogoBrain } from '../../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/portal/auth/login`, formData);
      localStorage.setItem('portal_token', res.data.token);
      localStorage.setItem('portal_user', JSON.stringify(res.data.user));
      toast.success('Welcome to the Customer Portal!');
      navigate('/portal/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px]"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to main site
          </Link>
          <div className="flex items-center justify-center gap-3 mb-4">
            <LogoBrain size={56} animate={true} />
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white">FlowFeed</h1>
              <p className="text-xs text-purple-300 uppercase tracking-wider">Customer Portal</p>
            </div>
          </div>
        </div>
        
        <Card className="border-white/10 bg-white/5 backdrop-blur-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-white">Welcome Back</CardTitle>
            <CardDescription className="text-white/60">
              Sign in to access your invoices, projects, and more
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white/80">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/80">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-white/40 text-sm">
                Don't have portal access?{' '}
                <a href="mailto:support@flowfeed.com" className="text-purple-400 hover:text-purple-300">
                  Contact your account manager
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-white/30 text-sm mt-8">
          © 2025 FlowFeed ERP. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PortalLogin;
